import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { FiAlertTriangle } from 'react-icons/fi';
import { apiGet } from '@/lib/api';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Badge } from '@/shared/components/ui/Badge';
import type { FailedPaymentItem } from '../types';

interface FailedPaymentsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const formatDateTime = (iso: string | undefined) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

export const FailedPaymentsDrawer: React.FC<FailedPaymentsDrawerProps> = ({ isOpen, onClose }) => {
  const { data: items, isLoading } = useQuery({
    queryKey: ['admin-dashboard-failed-payments'],
    queryFn: () => apiGet<FailedPaymentItem[]>('/dashboard/failed-payments'),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      icon={<FiAlertTriangle className="w-5 h-5" />}
      title="Pagamentos com Falha"
      subtitle={items ? `${items.length} empresa${items.length !== 1 ? 's' : ''} com problema` : undefined}
    >
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={92} variant="rectangular" />
          ))}
        </div>
      ) : items && items.length > 0 ? (
        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.companyId}
              className="p-3 rounded-lg space-y-2"
              style={{ border: '1px solid var(--theme-card-border)' }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate" style={{ color: 'var(--theme-card-text)' }}>
                    {item.companyName}
                  </p>
                  <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
                    {item.planName}
                  </p>
                </div>
                <Badge variant={item.failureStatus === 'past_due' ? 'error' : 'warning'}>
                  {item.failureStatus === 'past_due' ? 'Past Due' : 'Suspenso'}
                </Badge>
              </div>

              <div className="text-xs space-y-1" style={{ color: 'var(--theme-muted-text)' }}>
                <p>
                  <strong>Erro:</strong> {item.lastError || 'Erro não detalhado'}
                </p>
                <p>
                  <strong>{item.failedAttempts}</strong> tentativa{item.failedAttempts !== 1 ? 's' : ''} ·
                  Última em {formatDateTime(item.lastAttemptAt)}
                </p>
                <p>
                  Próxima retentativa: {item.nextRetryAt ? formatDateTime(item.nextRetryAt) : 'Aguarda reativação manual'}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16">
          <FiAlertTriangle className="w-12 h-12 mb-3" style={{ color: 'var(--theme-muted-text)', opacity: 0.5 }} />
          <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
            Nenhum pagamento com falha
          </p>
        </div>
      )}
    </Drawer>
  );
};
